import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Chip,
  Button,
  Tooltip,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  CircularProgress
} from '@mui/material';
import {
  RemoveCircle,
  Inventory,
  LocalGasStation,
  Info,
  LocationOn,
  Favorite as FavoriteIcon,
  VpnKey,
  PriorityHigh,
  CameraAlt
} from '@mui/icons-material';
import SpeedIcon from '@mui/icons-material/Speed';
import LocalShipping from '@mui/icons-material/LocalShipping';
import { Vehicle } from '@otw/models/inventory';
import { TransporterPackageWithVehicles } from '@otw/models';
import { usePhotosForInventoryObject } from 'hooks/inventory/usePhotosForInventoryObject';
import { formatDistance } from 'core/utils/inventoryUtils';
import StatusBar from './StatusBar';
import {
  boxTruck as boxTruckIcon,
  cargoVan as cargoVanIcon,
  cutaway as cutawayIcon,
  stepVan as stepVanIcon,
} from 'assets';
import { getVehiclePlaceholder } from '../VehicleUtils';

// Helper component to display vehicle image
export const VehicleImage = ({ vehicle }: { vehicle: Vehicle }) => {
  const { data: photos, isLoading } = usePhotosForInventoryObject('master_inventory', vehicle.id, {
    onlyMainPhoto: true,
    enabled: true
  });
  
  const fallbackImage = getVehicleIcon(vehicle.type);
  
  if (isLoading) {
    return (
      <Box 
        sx={{ 
          height: 140, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'grey.100'
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }
  
  if (!photos || photos.length === 0) {
    return (
      <CardMedia
        component="img"
        height="140"
        image={fallbackImage}
        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
        sx={{ objectFit: 'contain', bgcolor: 'background.subtle' }}
      />
    );
  }
  
  return (
    <CardMedia
      component="img"
      height="140"
      image={photos[0].url}
      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
      sx={{ objectFit: 'cover' }}
    />
  );
};

// Helper function to get vehicle icon based on type
export const getVehicleIcon = (type?: string | null) => {
  if (!type) return getVehiclePlaceholder();
  
  const typeLower = type.toLowerCase();
  if (typeLower.includes('box')) {
    return boxTruckIcon;
  } else if (typeLower.includes('cargo')) {
    return cargoVanIcon;
  } else if (typeLower.includes('cutaway')) {
    return cutawayIcon;
  } else if (typeLower.includes('step')) {
    return stepVanIcon;
  }
  
  return getVehiclePlaceholder();
};

// Helper function to format package status
export const formatPackageStatus = (status: string) => {
  switch (status) {
    case 'DRAFT':
      return 'Draft';
    case 'ASSIGNED':
      return 'Assigned';
    case 'IN_PROGRESS':
      return 'In Progress';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return status;
  }
};

// Notes button component
export const NotesButton = ({ notes }: { notes?: string | null }) => {
  if (!notes) return null;
  
  return (
    <Tooltip title={notes}>
      <IconButton size="small" sx={{ ml: 0.25, mt: -1 }}>
        <Info fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

// Action button configuration
export interface VehicleCardAction {
  label: string;
  icon: React.ReactNode;
  onClick: (vehicle: Vehicle) => void;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'error' | 'success' | 'warning' | 'info';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}

// Main VehicleCard props interface
export interface VehicleCardProps {
  vehicle: Vehicle;
  // Styling options
  height?: string | number;
  // Action buttons
  actions?: VehicleCardAction[];
  // Favorite
  favorite?: boolean;
  // Custom content
  customContent?: React.ReactNode;
  // Event handlers
  onImageClick?: (vehicle: Vehicle) => void;
  onTitleClick?: (vehicle: Vehicle) => void;
  onManagePhotos?: (vehicle: Vehicle) => void;
  // Status bar for transporter management
  showStatusBar?: boolean;
  package?: TransporterPackageWithVehicles;
  // Company information
  companyName?: string;
  // Additional props
  sx?: any;
  className?: string;
}

// Create a memoized VehicleCard component
const VehicleCard = React.memo(({ 
  vehicle,
  height,
  actions = [],
  favorite = false,
  customContent,
  onImageClick,
  onTitleClick,
  onManagePhotos,
  showStatusBar = false,
  package: pkg,
  companyName,
  sx = {},
  className
}: VehicleCardProps) => {
  
  
  const getCardHeight = () => {
    return height || '100%';
  };

  return (
    <Card 
      className={className}
      sx={{ 
        height: getCardHeight(),
        display: 'flex', 
        flexDirection: 'column',
        position: 'relative',
        border: (pkg?.vehicle_priorities?.[vehicle.id] || pkg?.vehicle_ready?.[vehicle.id]) ? '2px solid' : '1px solid',
        borderColor: pkg?.vehicle_priorities?.[vehicle.id] ? 'error.main' : pkg?.vehicle_ready?.[vehicle.id] ? 'success.main' : 'divider',
        ...sx
      }}
    >

      
      {/* Vehicle Image */}
      <Box 
        sx={{ position: 'relative', cursor: onImageClick ? 'pointer' : 'default' }}
        onClick={() => onImageClick?.(vehicle)}
      >
        <VehicleImage vehicle={vehicle} />
      </Box>
      
      <CardContent sx={{ flexGrow: 1, pb: 2 }}>
        {/* Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography 
            variant="h6" 
            component="div" 
            sx={{ 
              cursor: onTitleClick ? 'pointer' : 'default',
              flexGrow: 1
            }}
            onClick={() => onTitleClick?.(vehicle)}
          >
            {vehicle.year} {vehicle.make} {vehicle.model}
          </Typography>
          <NotesButton notes={vehicle.truck_notes} />
          {onManagePhotos && (
            <Tooltip title="View Photos">
              <IconButton 
                size="small" 
                sx={{ ml: 0.25, mt: -1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onManagePhotos(vehicle);
                }}
              >
                <CameraAlt fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        {/* High Priority Indicator */}
        {pkg?.vehicle_priorities?.[vehicle.id] && (
          <Box sx={{ mb: 1 }}>
            <Chip
              icon={<PriorityHigh />}
              label="HIGH PRIORITY"
              size="small"
              color="error"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                fontWeight: 600
              }}
            />
          </Box>
        )}
        
        {/* Ready for Pickup Indicator */}
        {pkg?.vehicle_ready?.[vehicle.id]?.is_ready && (
          <Box sx={{ mb: 1 }}>
            <Chip
              label={`READY FOR PICKUP${pkg.vehicle_ready[vehicle.id].ready_date !== 'TBD' ? ` - ${pkg.vehicle_ready[vehicle.id].ready_date}` : ''}`}
              size="small"
              color="success"
              sx={{
                fontSize: '0.6rem',
                height: 20,
                fontWeight: 600
              }}
            />
          </Box>
        )}
        
        {/* Installation Needs Indicators */}
        {pkg?.vehicle_needs?.[vehicle.id] && (
          <Box sx={{ mb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {pkg.vehicle_needs[vehicle.id].needs_cameras && (
              <Chip
                icon={<CameraAlt />}
                label="Needs Cameras"
                size="small"
                color="info"
                sx={{
                  fontSize: '0.6rem',
                  height: 20,
                  fontWeight: 600
                }}
              />
            )}
            {pkg.vehicle_needs[vehicle.id].needs_backup_sensors && (
              <Chip
                label="Needs Backup Sensors"
                size="small"
                color="info"
                sx={{
                  fontSize: '0.6rem',
                  height: 20,
                  fontWeight: 600
                }}
              />
            )}
            {pkg.vehicle_needs[vehicle.id].needs_anti_theft && (
              <Chip
                label="Needs Anti-Theft"
                size="small"
                color="info"
                sx={{
                  fontSize: '0.6rem',
                  height: 20,
                  fontWeight: 600
                }}
              />
            )}
          </Box>
        )}
        
        {/* Type and Size */}
        <Typography variant="body2" gutterBottom color="text.secondary">
          {vehicle.type} {vehicle.size && `- ${vehicle.size}`}
        </Typography>
        
        {/* Stock Number and VIN */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary" noWrap>
             VIN: {vehicle.vin || 'N/A'}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Stock #: {vehicle.stock_number || 'N/A'}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Bought from: {vehicle.bought_from?.trim() || '—'}
        </Typography>
        
        {/* Who */}
        {vehicle.who && (
          <Box sx={{ mb: 1 }}>
            <Chip 
              label={vehicle.who} 
              size="small" 
              variant="outlined"
              color="secondary"
              sx={{ maxWidth: '100%' }}
            />
          </Box>
        )}
        
        {/* Additional Info Chips */}
        <Box display="flex" flexWrap="wrap" gap={1} mt={1}>
          {vehicle.miles && (
            <Chip 
              icon={<SpeedIcon fontSize="small" />} 
              label={formatDistance(vehicle.miles)} 
              size="small" 
              variant="outlined"
            />
          )}
          {vehicle.fuel && (
            <Chip 
              icon={<LocalGasStation fontSize="small" />} 
              label={vehicle.fuel} 
              size="small" 
              variant="outlined"
            />
          )}
          {vehicle.cameras && (
            <Chip 
              icon={<CameraAlt fontSize="small" />} 
              label={`Cameras: ${vehicle.cameras.charAt(0).toUpperCase() + vehicle.cameras.slice(1)}`} 
              size="small" 
              variant="outlined"
              color={
                vehicle.cameras === 'yes' ? 'success' :
                vehicle.cameras === 'no' ? 'error' :
                'default'
              }
            />
          )}
          {vehicle.shelves && (
            <Chip 
              icon={<Inventory fontSize="small" />} 
              label={`Shelves: ${vehicle.shelves.charAt(0).toUpperCase() + vehicle.shelves.slice(1)}`} 
              size="small" 
              variant="outlined"
              color={
                vehicle.shelves === 'yes' ? 'success' :
                vehicle.shelves === 'no' ? 'error' :
                'default'
              }
            />
          )}
        </Box>
        
        {/* Status Bar for Transporter Management */}
        {showStatusBar && pkg && (
          <Box sx={{ mt: 1 }}>
            <StatusBar 
              vehicle={vehicle} 
              package={pkg} 
            />
          </Box>
        )}
        
        {/* Package Information Display */}
        {pkg && (
          <Box sx={{
            mt: 1,
            p: 1,
            backgroundColor: 'rgba(0,0,0,0.7)',
            color: 'white',
            borderRadius: 1,
            backdropFilter: 'blur(4px)'
          }}>
            <Typography variant="caption" display="block" noWrap>
              Package: {pkg.name}
            </Typography>
            <Typography variant="caption" display="block" noWrap>
              Company: {companyName || 'Unassigned'}
            </Typography>
          </Box>
        )}
        
        
        {/* Custom Content */}
        {customContent}
      </CardContent>
      
      {/* Action Buttons */}
      {actions.length > 0 && (
        <CardActions sx={{ pt: 0, justifyContent: 'center', flexDirection: 'column', width: '100%' }}>
          {actions.map((action, index) => (
            <Button
              key={index}
              variant={action.variant || 'outlined'}
              color={action.color || 'primary'}
              startIcon={action.loading ? <CircularProgress size={18} /> : action.icon}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick(vehicle);
              }}
              disabled={action.disabled || action.loading}
              fullWidth={action.fullWidth !== false}
              sx={{ 
                fontSize: { xs: '0.8rem', md: '1rem' },
                mb: index < actions.length - 1 ? 0.5 : 0
              }}
            >
              {action.loading ? 'Loading...' : action.label}
            </Button>
          ))}
        </CardActions>
      )}
      
    
    </Card>
  );
});



export default VehicleCard;
